import { createAsyncThunk } from "@reduxjs/toolkit";
import { AdminLoginRequest, AdminRegisterRequest, AdminLoginResponse } from "../interfaces/adminAuth.interface";
import { adminAuthService } from "../services/adminAuth.service";
import { API_CONFIG } from "../../../utils/apiConfig";

function stripLargeFields(admin: any) {
  if (!admin) return admin;
  const { profileImage, ...rest } = admin;
  return profileImage ? { ...rest, profileImage: profileImage.startsWith("data:") ? "[image]" : profileImage } : rest;
}

function saveAdmin(admin: any) {
  localStorage.setItem(API_CONFIG.ADMIN_USER_STORAGE_KEY, JSON.stringify(stripLargeFields(admin)));
}

export const adminLogin = createAsyncThunk<
  AdminLoginResponse,
  AdminLoginRequest
>("adminAuth/login", async (credentials, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.login(credentials);
    if (response.success && response.data) {
      const { accessToken, refreshToken, admin } = response.data;
      if (accessToken && refreshToken && admin) {
        localStorage.setItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY, refreshToken);
        saveAdmin(admin);
      }
      return response.data;
    }
    return rejectWithValue("Login failed");
  } catch (error: any) {
    return rejectWithValue(error.message || "Login failed");
  }
});

export const adminSignup = createAsyncThunk<
  { adminId: string; message: string },
  AdminRegisterRequest
>("adminAuth/signup", async (data, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.register(data);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue("Registration failed");
  } catch (error: any) {
    return rejectWithValue(error.message || "Registration failed");
  }
});

export const adminLogout = createAsyncThunk(
  "adminAuth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await adminAuthService.logout();
    } catch {
      // Logout locally even if server fails
    } finally {
      localStorage.removeItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
      localStorage.removeItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
      localStorage.removeItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
    }
  }
);

export const adminRefreshToken = createAsyncThunk<
  { accessToken: string; refreshToken: string }
>("adminAuth/refresh", async (_, { rejectWithValue }) => {
  try {
    const refreshToken = localStorage.getItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new Error("No refresh token");
    const response = await adminAuthService.refreshToken(refreshToken);
    if (response.success && response.data) {
      localStorage.setItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY, response.data.accessToken);
      localStorage.setItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY, response.data.refreshToken);
      return response.data;
    }
    return rejectWithValue("Token refresh failed");
  } catch (error: any) {
    localStorage.removeItem(API_CONFIG.ADMIN_TOKEN_STORAGE_KEY);
    localStorage.removeItem(API_CONFIG.ADMIN_REFRESH_TOKEN_KEY);
    localStorage.removeItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
    return rejectWithValue(error.message || "Token refresh failed");
  }
});

export const adminGetProfile = createAsyncThunk(
  "adminAuth/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminAuthService.getProfile();
      const adminData = response.data && (response.data as any).admin
        ? (response.data as any).admin
        : (response.data as any);
      if (response.success && adminData) {
        saveAdmin(adminData);
        return adminData;
      }
      return rejectWithValue("Failed to get profile");
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to get profile");
    }
  }
);

export const verifyAdminEmail = createAsyncThunk<
  void,
  { email: string; verificationCode: string }
>("adminAuth/verifyEmail", async ({ email, verificationCode }, { rejectWithValue }) => {
  try {
    await adminAuthService.verifyEmail(email, verificationCode);
  } catch (error: any) {
    return rejectWithValue(error.message || "Verification failed");
  }
});

export const resendAdminVerification = createAsyncThunk<
  void,
  { email: string }
>("adminAuth/resendVerification", async ({ email }, { rejectWithValue }) => {
  try {
    await adminAuthService.resendVerification(email);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to resend verification");
  }
});

export const verifyAdminLoginOTP = createAsyncThunk<
  { user: any },
  { email: string; code: string }
>("adminAuth/verifyLoginOTP", async ({ email, code }, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.verifyOTP(email, code);
    if (response.success && response.data) {
      return response.data;
    }
    return rejectWithValue("Invalid verification code");
  } catch (error: any) {
    return rejectWithValue(error.message || "Invalid verification code");
  }
});

export const adminUpdateProfile = createAsyncThunk<
  any,
  Record<string, unknown>
>("adminAuth/updateProfile", async (data, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.updateProfile(data);
    const adminData = response.data && (response.data as any).admin
      ? (response.data as any).admin
      : (response.data as any);
    if (response.success && adminData) {
      saveAdmin(adminData);
      return adminData;
    }
    return rejectWithValue("Failed to update profile");
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update profile");
  }
});

export const adminUploadProfileImage = createAsyncThunk<
  { profileImage: string },
  File
>("adminAuth/uploadProfileImage", async (file, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.uploadProfileImage(file);
    if (response.success && response.data) {
      const profileImage = response.data.profileImage || response.data.admin?.profileImage;
      const stored = localStorage.getItem(API_CONFIG.ADMIN_USER_STORAGE_KEY);
      if (stored) {
        const admin = JSON.parse(stored);
        admin.profileImage = profileImage?.startsWith("data:") ? "[image]" : profileImage;
        saveAdmin(admin);
      }
      return { profileImage };
    }
    return rejectWithValue("Failed to upload profile image");
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to upload profile image");
  }
});

export const adminChangePassword = createAsyncThunk<
  void,
  { currentPassword: string; newPassword: string; confirmPassword: string }
>("adminAuth/changePassword", async (data, { rejectWithValue }) => {
  try {
    await adminAuthService.changePassword(data);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to change password");
  }
});

export const adminEnable2FA = createAsyncThunk<
  void,
  { method: string; phoneNumber?: string; phoneCode?: string }
>("adminAuth/enable2FA", async (data, { rejectWithValue }) => {
  try {
    await adminAuthService.enable2FA(data);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to enable 2FA");
  }
});

export const adminDisable2FA = createAsyncThunk<
  void,
  { email: string; password: string }
>("adminAuth/disable2FA", async (data, { rejectWithValue }) => {
  try {
    await adminAuthService.disable2FA(data);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to disable 2FA");
  }
});

export const adminGet2FAStatus = createAsyncThunk<
  { isEnabled: boolean; method?: string }
>("adminAuth/get2FAStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.get2FAStatus();
    if (response.success && response.data) {
      return response.data;
    }
    return { isEnabled: false };
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to get 2FA status");
  }
});

export const adminVerify2FACode = createAsyncThunk<
  void,
  string
>("adminAuth/verify2FACode", async (code, { rejectWithValue }) => {
  try {
    await adminAuthService.verify2FACode(code);
  } catch (error: any) {
    return rejectWithValue(error.message || "Invalid 2FA code");
  }
});

export const adminResend2FACode = createAsyncThunk<
  void
>("adminAuth/resend2FACode", async (_, { rejectWithValue }) => {
  try {
    await adminAuthService.resend2FACode();
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to resend 2FA code");
  }
});

export const adminGetSessions = createAsyncThunk<
  any[],
  { page: number; limit: number; append?: boolean }
>("adminAuth/getSessions", async ({ page, limit }, { rejectWithValue }) => {
  try {
    const response = await adminAuthService.getSessions({ page, limit });
    if (response.success && response.data) {
      return response.data.sessions || [];
    }
    return [];
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to get sessions");
  }
});

export const adminRevokeSession = createAsyncThunk<
  void,
  string
>("adminAuth/revokeSession", async (sessionId, { rejectWithValue }) => {
  try {
    await adminAuthService.revokeSession(sessionId);
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to revoke session");
  }
});

export const adminRevokeAllOtherSessions = createAsyncThunk<
  void
>("adminAuth/revokeAllOtherSessions", async (_, { rejectWithValue }) => {
  try {
    await adminAuthService.revokeAllOtherSessions();
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to revoke sessions");
  }
});

export const adminVerifyCurrentPassword = createAsyncThunk<
  void,
  string
>("adminAuth/verifyCurrentPassword", async (password, { rejectWithValue }) => {
  try {
    await adminAuthService.verifyCurrentPassword(password);
  } catch (error: any) {
    return rejectWithValue(error.message || "Invalid password");
  }
});
